import React from "react";
import "./admin.css";
import "@fontsource/jetbrains-mono";
import "@fontsource/rubik";
import { Rebase, RebaseAuth, useRebaseAuthController } from "@rebasepro/app";
import { RebaseCMS, RebaseShell } from "@rebasepro/cms";
import { RebaseStudio } from "@rebasepro/studio";
import { ErrorBoundary } from "@rebasepro/ui";
import { collections } from "virtual:rebase-collections";
import { client } from "./lib/rebaseClient";
import { PipelineHome } from "./views/PipelineHome";
import { InboxView } from "./views/InboxView";
import { emailActions } from "./actions/emailActions";
import { adminCollections } from "./adminCollections";

// Built once, at module load: the admin keys its collection registry on these
// objects, and a fresh array every render would have it re-resolve them all.
const collectionsForAdmin = adminCollections(collections);

// Admin-only app. The home page is the pipeline dashboard — where the work is
// piled up and what the last run of each job said — because the boards alone
// cannot explain an empty board. Studio stays mounted for triggering the crons
// by hand and reading their live logs.
//
// The navigation groups are ordered to match the flow (Inbox → Signals →
// Companies → People → Emails → Suppressions), so the sidebar reads as the
// pipeline it is rather than as an alphabetical list of tables. The inbox is
// first because it is the only group holding something a person is waiting on. Companies sits second because
// it is the other intake, not a later stage: a signal is something a person
// said, a company is something a company shipped, and both feed the same
// review habit.

export function AdminApp() {
    const authController = useRebaseAuthController({ client });

    return (
        <ErrorBoundary fullPage>
            <Rebase client={client} authController={authController} entityActions={emailActions}>
                <RebaseAuth />
                <RebaseCMS
                    collections={collectionsForAdmin}
                    homePage={<PipelineHome />}
                    views={[
                        {
                            slug: "inbox",
                            name: "Inbox",
                            icon: "Inbox",
                            description: "Every mailbox's conversations, in both directions, with a box to answer in.",
                            view: <InboxView />
                        }
                    ]}
                    entityActions={emailActions}
                    navigationGroupMappings={[
                        { name: "Inbox", entries: ["inbox", "mailboxes", "email_templates", "email_sender_rules"] },
                        { name: "CRM", entries: ["prospects", "companies", "tasks", "activities"] },
                        { name: "Outreach", entries: ["leads", "emails", "suppressions", "citation_targets", "visibility_checks"] },
                        { name: "Diagnostics", entries: ["agent_runs", "mailbox_folders", "notifications", "model_spend"] }
                    ]}
                />
                <RebaseStudio />
                <RebaseShell title="rebase outreach" />
            </Rebase>
        </ErrorBoundary>
    );
}
